import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  CircularProgress,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../../../theme";
import { fetchDataEngine, graphqlQuery, generateAndExecuteMutation } from "../../../../data/Axios/DynamicService";
import { HRService } from "../../../../config";
import Swal from "sweetalert2";

const base_url = HRService.uri;

/**
 * ManageEmployeePayDialog - Dialog for managing employee pay components
 * Uses consistent UI styling but requires custom form logic for conditional fields
 */
const ManageEmployeePayDialog = ({ open, onClose, employeeData }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // UI State
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Data State
  const [payComponents, setPayComponents] = useState([]);
  const [allPayComponents, setAllPayComponents] = useState([]);

  // Edit State
  const [isEditing, setIsEditing] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  // Form State
  const [formValues, setFormValues] = useState({
    payComponentId: "",
    amount: "",
    percentOfBase: "",
    baseComponentCode: "",
    effectiveFrom: "",
    effectiveTo: "",
    isActive: true,
    notes: "",
  });

  const employeeId = employeeData?.id;

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = Number(decodedToken.Id) || null;

  // Table columns
  const columns = [
    { field: "payComponentName", headerName: "Component", flex: 1.5 },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      valueFormatter: (params) => params.value ?? "-"
    },
    {
      field: "percentOfBase",
      headerName: "% of Base",
      flex: 1,
      valueFormatter: (params) => params.value ?? "-"
    },
    {
      field: "baseComponentCode",
      headerName: "Base Code",
      flex: 1,
      valueFormatter: (params) => params.value ?? "-"
    },
    { field: "effectiveFrom", headerName: "Effective From", flex: 1 },
    {
      field: "effectiveTo",
      headerName: "Effective To",
      flex: 1,
      valueFormatter: (params) => params.value ?? "-"
    },
    {
      field: "isActive",
      headerName: "Status",
      flex: 0.8,
      valueFormatter: (params) => params.value ? "Active" : "Inactive"
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => handleEdit(params.row)}
          sx={{
            backgroundColor: colors.blueAccent[600],
            color: colors.grey[100],
            "&:hover": { backgroundColor: colors.blueAccent[700] },
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  const commonSx = {
    "& .MuiFilledInput-root": {
      backgroundColor: colors.primary[500],
    },
  };

  // Fetch employee pay components
  const fetchEmployeePay = async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const result = await fetchDataEngine(
        "employeePayComponentByEmployeeId",
        "employeePayComponent",
        { employeeId: Number(employeeId) },
        [
          "id",
          "payComponentId",
          "payComponentName",
          "amount",
          "percentOfBase",
          "baseComponentCode",
          "effectiveFrom",
          "effectiveTo",
          "isActive",
          "notes",
        ],
        base_url
      );
      setPayComponents(result || []);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all pay components for dropdown
  const fetchPayComponentsList = async () => {
    try {
      const result = await graphqlQuery(`{
                payComponent {
                    id
                    name
                    calculationBasis
                }
            }`, base_url);
      setAllPayComponents(result || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (open && employeeId) {
      fetchEmployeePay();
      fetchPayComponentsList();
      setShowForm(false);
      setEditingRow(null);
    }
  }, [open, employeeId]);

  const resetForm = () => {
    setFormValues({
      payComponentId: "",
      amount: "",
      percentOfBase: "",
      baseComponentCode: "",
      effectiveFrom: "",
      effectiveTo: "",
      isActive: true,
      notes: "",
    });
    setIsEditing(false);
    setEditingRow(null);
  };

  const handleShowAddForm = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (row) => {
    setIsEditing(true);
    setEditingRow(row);
    setFormValues({
      payComponentId: row.payComponentId || "",
      amount: row.amount || "",
      percentOfBase: row.percentOfBase || "",
      baseComponentCode: row.baseComponentCode || "",
      effectiveFrom: row.effectiveFrom || "",
      effectiveTo: row.effectiveTo || "",
      isActive: row.isActive ?? true,
      notes: row.notes || "",
    });
    setShowForm(true);
  };

  const handleFormChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formValues.payComponentId) {
      Swal.fire("Missing fields", "Please select a pay component", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        employeeId: Number(employeeId),
        payComponentId: Number(formValues.payComponentId),
        amount: formValues.amount ? parseFloat(formValues.amount) : null,
        percentOfBase: formValues.percentOfBase ? parseFloat(formValues.percentOfBase) : null,
        baseComponentCode: formValues.baseComponentCode || null,
        effectiveFrom: formValues.effectiveFrom || null,
        effectiveTo: formValues.effectiveTo || null,
        isActive: Boolean(formValues.isActive),
        notes: formValues.notes || null,
      };

      if (isEditing) {
        payload.id = editingRow.id;
        payload.createdBy = userId;
      } else {
        payload.createdBy = userId;
      }

      await generateAndExecuteMutation(
        isEditing ? "updateEmployeePayComponent" : "createEmployeePayComponent",
        payload,
        "employeePayComponent",
        base_url
      );

      Swal.fire("Success", `Pay component ${isEditing ? "updated" : "created"} successfully`, "success");
      fetchEmployeePay();
      resetForm();
      setShowForm(false);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to save pay component", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    setShowForm(false);
    onClose();
  };

  // Get selected component info for conditional fields
  const selectedComponent = allPayComponents.find((c) => c.id === Number(formValues.payComponentId));
  const baseComponentOptions = [
    { value: "BASIC", label: "BASIC" },
    { value: "GROSS", label: "GROSS" },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          backgroundColor: colors.primary[400],
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: colors.blueAccent[700],
          color: colors.grey[100],
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Employee Payment Structure
          </Typography>
          <Typography variant="body2" color={colors.grey[300]}>
            {employeeData?.firstName} {employeeData?.lastName}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: colors.grey[100] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: colors.primary[400] }}>
        {/* ADD BUTTON */}
        {!showForm && (
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              startIcon={<AddIcon />}
              onClick={handleShowAddForm}
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                "&:hover": { backgroundColor: colors.greenAccent[700] },
              }}
            >
              Add Pay Component
            </Button>
          </Box>
        )}

        {/* FORM */}
        {showForm && (
          <Box mb={3}>
            <Typography variant="h6" mb={2} color={colors.grey[100]}>
              {isEditing ? "Edit Pay Component" : "Add Pay Component"}
            </Typography>

            <Box display="grid" gridTemplateColumns="repeat(4,1fr)" gap={2}>
              {/* Pay Component Select */}
              <FormControl fullWidth variant="filled" sx={commonSx}>
                <InputLabel>Pay Component *</InputLabel>
                <Select
                  value={formValues.payComponentId}
                  onChange={(e) => handleFormChange("payComponentId", e.target.value)}
                >
                  {allPayComponents.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Amount - show for Fixed calculation basis */}
              {selectedComponent?.calculationBasis === "Fixed" && (
                <TextField
                  label="Amount *"
                  type="number"
                  variant="filled"
                  value={formValues.amount}
                  onChange={(e) => handleFormChange("amount", e.target.value)}
                  sx={commonSx}
                  fullWidth
                />
              )}

              {/* Percentage of Base - show for PercentOfBasic */}
              {selectedComponent?.calculationBasis === "PercentOfBasic" && (
                <>
                  <TextField
                    label="Percentage of Base (%) *"
                    type="number"
                    variant="filled"
                    value={formValues.percentOfBase}
                    onChange={(e) => handleFormChange("percentOfBase", e.target.value)}
                    sx={commonSx}
                    fullWidth
                  />
                  <FormControl fullWidth variant="filled" sx={commonSx}>
                    <InputLabel>Base Component Code</InputLabel>
                    <Select
                      value={formValues.baseComponentCode}
                      onChange={(e) => handleFormChange("baseComponentCode", e.target.value)}
                    >
                      {baseComponentOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}

              {/* Dates */}
              <TextField
                label="Effective From"
                type="date"
                variant="filled"
                value={formValues.effectiveFrom}
                onChange={(e) => handleFormChange("effectiveFrom", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={commonSx}
                fullWidth
              />

              <TextField
                label="Effective To"
                type="date"
                variant="filled"
                value={formValues.effectiveTo}
                onChange={(e) => handleFormChange("effectiveTo", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={commonSx}
                fullWidth
              />

              {/* Notes - full width */}
              <TextField
                label="Notes"
                variant="filled"
                value={formValues.notes}
                onChange={(e) => handleFormChange("notes", e.target.value)}
                sx={{ ...commonSx, gridColumn: "span 2" }}
                fullWidth
                multiline
                rows={2}
              />
            </Box>

            <Box mt={2} display="flex" gap={2}>
              <Button
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={submitting}
                sx={{
                  backgroundColor: colors.greenAccent[600],
                  color: colors.grey[100],
                  "&:hover": { backgroundColor: colors.greenAccent[700] },
                }}
              >
                {isEditing ? "Update" : "Save"}
              </Button>

              <Button
                startIcon={<CancelIcon />}
                variant="outlined"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
                sx={{
                  color: colors.grey[100],
                  borderColor: colors.grey[500],
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {/* TABLE */}
        {!showForm && (
          <>
            <Divider sx={{ my: 2 }} />
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress sx={{ color: colors.greenAccent[500] }} />
              </Box>
            ) : payComponents.length > 0 ? (
              <DataGrid
                rows={payComponents}
                columns={columns}
                autoHeight
                pageSize={5}
                getRowId={(row) => row.id}
                sx={{
                  border: "none",
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    backgroundColor: colors.primary[400],
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: colors.blueAccent[700],
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: `1px solid ${colors.grey[700]}`,
                  },
                }}
              />
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <Typography color="textSecondary">
                  No pay components found. Click 'Add Pay Component' to create one.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ManageEmployeePayDialog;
